import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './auth/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import PermissionRoute from './components/PermissionRoute'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import NodeListPage from './pages/NodeListPage'
import NodeDetailPage from './pages/NodeDetailPage'
import GuardianAlphaRestartResultPage from './pages/GuardianAlphaRestartResultPage'
import JobsPage from './pages/JobsPage'
import ConfigPage from './pages/ConfigPage'
import SecretsPage from './pages/SecretsPage'
import ReleasesPage from './pages/ReleasesPage'
import ServerCapacityPage from './pages/ServerCapacityPage'
import DeploymentLayout from './pages/deployment/DeploymentLayout'
import CandidateReviewPage from './pages/deployment/CandidateReviewPage'
import ComparisonsPage from './pages/deployment/ComparisonsPage'
import ComparisonDetailPage from './pages/deployment/ComparisonDetailPage'
import ModelRegistryPage from './pages/deployment/ModelRegistryPage'
import EventsPage from './pages/deployment/EventsPage'
import UsersPage from './pages/UsersPage'
import AuditLogsPage from './pages/AuditLogsPage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import GuardianAlphaConfigPage from './pages/GuardianAlphaConfigPage'
import ManualOperatingListPage from './pages/manual-operating/ManualOperatingListPage'
import ManualOperatingDetailPage from './pages/manual-operating/ManualOperatingDetailPage'

export const MANUAL_OPERATING_ENABLED =
  import.meta.env.VITE_MANUAL_OPERATING_ENABLED === 'true'

function NotFoundPage() {
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: '#333', marginBottom: 12 }}>404 — Page not found</h2>
      <p style={{ color: '#888', marginBottom: 24 }}>The page you requested does not exist.</p>
      <a href="/nodes" style={{ color: '#3949ab', textDecoration: 'none', fontSize: 14 }}>
        Go to Node list
      </a>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/nodes" replace />} />
            <Route
              path="nodes"
              element={
                <PermissionRoute permission="node:read">
                  <NodeListPage />
                </PermissionRoute>
              }
            />
            <Route
              path="nodes/:id"
              element={
                <PermissionRoute permission="node:read">
                  <NodeDetailPage />
                </PermissionRoute>
              }
            />
            <Route
              path="nodes/:nodeId/guardian-alpha/restart-result"
              element={
                <PermissionRoute permission="node:read">
                  <GuardianAlphaRestartResultPage />
                </PermissionRoute>
              }
            />
            <Route
              path="jobs"
              element={
                <PermissionRoute permission="node:read">
                  <JobsPage />
                </PermissionRoute>
              }
            />
            <Route
              path="server"
              element={
                <PermissionRoute permission="node:read">
                  <ServerCapacityPage />
                </PermissionRoute>
              }
            />
            <Route
              path="config"
              element={
                <PermissionRoute permission="config:read">
                  <ConfigPage />
                </PermissionRoute>
              }
            />
            <Route
              path="guardian-alpha/config"
              element={
                <PermissionRoute permission="config:read">
                  <GuardianAlphaConfigPage />
                </PermissionRoute>
              }
            />
            {MANUAL_OPERATING_ENABLED && (
              <>
                <Route
                  path="guardian-alpha/manual-operating"
                  element={
                    <PermissionRoute permission="config:read">
                      <ManualOperatingListPage />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="guardian-alpha/manual-operating/:targetId"
                  element={
                    <PermissionRoute permission="config:read">
                      <ManualOperatingDetailPage />
                    </PermissionRoute>
                  }
                />
              </>
            )}
            <Route
              path="secrets"
              element={
                <PermissionRoute permission="secret:manage">
                  <SecretsPage />
                </PermissionRoute>
              }
            />
            <Route
              path="releases"
              element={
                <PermissionRoute permission="agent-release:manage">
                  <ReleasesPage />
                </PermissionRoute>
              }
            />
            <Route
              path="users"
              element={
                <PermissionRoute permission="user:manage">
                  <UsersPage />
                </PermissionRoute>
              }
            />
            <Route
              path="audit-logs"
              element={
                <PermissionRoute permission="audit:read">
                  <AuditLogsPage />
                </PermissionRoute>
              }
            />
            <Route path="account/change-password" element={<ChangePasswordPage />} />
            <Route
              path="deployment"
              element={
                <PermissionRoute permission="node:read">
                  <DeploymentLayout />
                </PermissionRoute>
              }
            >
              <Route index element={<Navigate to="/deployment/candidates" replace />} />
              <Route path="candidates" element={<CandidateReviewPage />} />
              <Route path="comparisons" element={<ComparisonsPage />} />
              <Route path="comparisons/:id" element={<ComparisonDetailPage />} />
              <Route path="model-registry" element={<ModelRegistryPage />} />
              <Route path="events" element={<EventsPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
